import { useState, useEffect, useRef } from 'react';
import { useOpportunities } from '../contexts/OpportunitiesContext';
import type { ArbitrageOpportunity } from '../types/opportunity';

/**
 * Custom hook that subscribes to opportunity updates from OpportunitiesContext.
 *
 * This hook provides real-time opportunity data updates via WebSocket and tracks
 * when the data changes for visual feedback (pulse effects).
 *
 * PERFORMANCE: This hook updates synchronously with context changes for maximum speed.
 * It watches the global lastUpdate timestamp to catch every update cycle (~75ms).
 *
 * @param opportunityKey - The unique key for the opportunity
 * @returns Object containing opportunity data, update state, and availability
 */
export function useOpportunityLiveUpdate(opportunityKey: string) {
  // Get opportunities from Context (not Redux!)
  const { opportunities, lastUpdate } = useOpportunities();

  const [lastUpdateTime, setLastUpdateTime] = useState(Date.now());
  const [hasDataChanged, setHasDataChanged] = useState(false);
  const prevOpportunityRef = useRef<ArbitrageOpportunity | undefined>(undefined);

  // Get the specific opportunity by key - directly from context for immediate updates
  const opportunity: ArbitrageOpportunity | undefined = opportunities[opportunityKey];

  // Detect changes by comparing with previous opportunity
  // This ensures we update on EVERY context update cycle
  useEffect(() => {
    const currentOpp = opportunities[opportunityKey];

    // Check if opportunity actually changed (by reference or by lastUpdate timestamp)
    if (currentOpp && (currentOpp !== prevOpportunityRef.current || lastUpdate > lastUpdateTime)) {
      prevOpportunityRef.current = currentOpp;
      setLastUpdateTime(Date.now());
      setHasDataChanged(true);

      // Reset pulse effect after animation completes
      const timer = setTimeout(() => setHasDataChanged(false), 500);
      return () => clearTimeout(timer);
    }
  }, [opportunities, opportunityKey, lastUpdate, lastUpdateTime]); // Watch opportunities object and lastUpdate

  return {
    opportunity,
    lastUpdateTime,
    hasDataChanged,
    isAvailable: opportunity !== undefined
  };
}

/**
 * Format time since update as "Xs ago" or "Xm ago".
 *
 * @param timestamp - The timestamp in milliseconds
 * @returns Formatted string like "2s ago" or "3m ago"
 */
export function formatTimeSinceUpdate(timestamp: number): string {
  const seconds = Math.floor((Date.now() - timestamp) / 1000);

  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  return `${minutes}m ago`;
}
